/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { PropsWithChildren } from 'react'

import styled, { css } from 'styled-components'

import { pxToVw } from 'src/functions/units'

type FlexAttributes = {
  alignContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'stretch'
    | 'start'
    | 'end'
    | 'baseline'
    | 'first baseline'
    | 'last baseline'
  alignItems?:
    | 'stretch'
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'baseline'
    | 'first baseline'
    | 'last baseline'
    | 'start'
    | 'end'
    | 'self-start'
    | 'self-end'
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'
  columnGap?: number
  inline?: boolean
  flex?: number | string
  flexDirection?: 'row' | 'row-reverse' | 'column' | 'column-reverse'
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
    | 'start'
    | 'end'
    | 'left'
    | 'right'
  order?: number
  rowGap?: number
  wrap?: 'nowrap' | 'wrap' | 'wrap-reverse'
}

type Props = PropsWithChildren<
  {
    as?: keyof JSX.IntrinsicElements
    className?: string
  } & FlexAttributes
>

const FlexBox = ({
  // Flex attributes
  alignContent,
  alignItems = 'flex-start',
  alignSelf,
  columnGap = 0,
  inline = false,
  flex,
  flexDirection = 'row',
  justifyContent = 'flex-start',
  order,
  rowGap = 0,
  wrap,
  // Element attributes
  as,
  children,
  className
}: Props) => {
  return (
    <StyledFlexBox
      $alignContent={alignContent}
      $alignItems={alignItems}
      $alignSelf={alignSelf}
      $columnGap={columnGap}
      $inline={inline}
      $flex={flex}
      $flexDirection={flexDirection}
      $justifyContent={justifyContent}
      $order={order}
      $rowGap={rowGap}
      $wrap={wrap}
      as={as}
      children={children}
      className={className}
    />
  )
}

export default FlexBox

const StyledFlexBox = styled.div<{
  [K in keyof FlexAttributes as `$${K}`]: FlexAttributes[K]
}>`
  ${({
    $alignContent,
    $alignItems,
    $alignSelf,
    $columnGap,
    $inline,
    $flex,
    $flexDirection,
    $justifyContent,
    $order,
    $rowGap,
    $wrap
  }) => css`
    display: ${$inline ? 'inline-flex' : 'flex'};
    align-content: ${$alignContent};
    align-items: ${$alignItems};
    align-self: ${$alignSelf};
    flex: ${$flex};
    flex-direction: ${$flexDirection};
    flex-wrap: ${$wrap};
    gap: ${($rowGap || $columnGap) &&
    `${$rowGap ? pxToVw($rowGap) : 0} ${$columnGap ? pxToVw($columnGap) : 0}`};
    justify-content: ${$justifyContent};
    order: ${$order};

    ${({ theme }) => css`
      ${theme.mediaQuery.mobile`
        gap: ${
          ($rowGap || $columnGap) &&
          `${$rowGap ? pxToVw($rowGap, 720) : 0} ${$columnGap ? pxToVw($columnGap, 720) : 0}`
        };
      `}
    `}
  `}
`
